// LeadBoard.js
import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Alert, Snackbar } from "@mui/material";

import AccountStageColumn from "./AccountStageColumn";
import AccountQuickView from "../popup";
import { useApiCall } from "../../../../../component/custom-hooks/api-call";
import InnerLoader from "../../../../../component/ui/loader/inner-loader";

export default function AccountBoard({
  filters,
  groupBy = "special",
  sendFilters,
  ProductList = [],
  PackageList = [],
  DealStages = [],
  ClientList = [],
  UserList = [],
  onLoadMoreClients,
  hasMoreClients,
  isLoadingClients,
  country,
  SubProductList = [],
  fetchSubProducts = () => {},
  fetchPackages = () => {},
  CustomerData = {},
  BrandList = {},
}) {
  const [leads, setLeads] = useState([]);
  const [stages, setStages] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLead, setSelectedLead] = useState(null);
  const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
  const [pagination, setPagination] = useState({});
  const [isApiPending, setIsApiPending] = useState(false);
  const [alertData, setAlertData] = useState({
    isShow: false,
    type: "success",
    message: "",
  });
  const [counts, setCounts] = useState({});
  const [cardLoad, setCardload] = useState(false);

  const divRef = useRef(null);
  const isApiPendingRef = useRef(false);
  const apiCall = useApiCall();

  const handleCloseAlert = () =>
    setAlertData({ isShow: false, type: "", message: "" });

  useEffect(() => {
    // reset state when groupBy changes
    setIsLoading(true);
    setLeads([]);
    setStages([]);
    setPagination({});
    loadLeadsAndStages(1);
  }, [filters, groupBy]);

  const onSuccess = (message) => {
    setAlertData({ isShow: true, type: "success", message: message });
    setIsLoading(true);
    loadLeadsAndStages(1);
  };

  const onError = (message) => {
    setAlertData({ isShow: true, type: "error", message: message });
  };

  const loadLeadsAndStages = async (page = "") => {
    // build query with filters (same format as list)
    let extraUrl = `?group_by=${groupBy}&page=${page}`;
    if (filters?.length) {
      for (let i = 0; i < filters.length; i++) {
        // skip empty values
        if (
          filters[i]?.value !== undefined &&
          filters[i]?.value !== null &&
          filters[i]?.value !== ""
        ) {
          extraUrl += `&${filters[i].name}=${filters[i].value}`;
        }
      }
    }

    setIsApiPending(true);
    try {
      // use constructed extraUrl
      const res = await apiCall({
        method: "get",
        url: `accounts/kanban${extraUrl}`,
      });

      sendFilters(res?.custom_filters);
      setCounts(res.counts);

      if (res.status && res.data) {
        const data = res.data.data || res.data;

        if (page === 1) {
          // fresh load
          const dynamicStages = Object.keys(data).map((key) => ({
            id: key,
            name: key.replace(/_/g, " "),
          }));
          setStages(dynamicStages);
        } else {
          // append new stages if not already added
          const newStages = Object.keys(data).map((key) => ({
            id: key,
            name: key.replace(/_/g, " "),
          }));

          setStages((prevStages) => {
            const existingIds = new Set(prevStages.map((s) => s.id));
            const uniqueNew = newStages.filter((s) => !existingIds.has(s.id));
            return [...prevStages, ...uniqueNew];
          });
        }

        // Flatten leads from all stages
        const newLeads = Object.entries(data).flatMap(([stageId, stageLeads]) =>
          stageLeads.map((lead, index) => {
            const metaMap = Object.fromEntries(
              (lead.meta || []).map((m) => [m.meta_key, m.meta_value])
            );
            return {
              id: lead.userid?.toString() || index.toString(),
              name: lead.name,
              service: metaMap.product || "",
              source: lead?.source,
              current_stage: stageId,
              order: index,
              contact_name: lead.name || "",
              contact_email: lead.email || "",
              contact_phone: lead.phone || "",
              company_name: lead.companyname || "",
              notes: metaMap.message || lead.message || "",
              leadkey: lead.leadkey || lead.leadid,
              assigned:
                Array.isArray(lead?.assigned_users) &&
                lead.assigned_users.length > 0
                  ? lead.assigned_users
                  : Array.isArray(lead?.assigned_user_details) &&
                    lead.assigned_user_details.length > 0
                  ? lead.assigned_user_details
                  : [],

              time: lead?.signuptime,
              country: lead?.country,
              completeLead: lead,
            };
          })
        );

        // Replace on page 1, append on next pages
        setLeads((prev) => (page === 1 ? newLeads : [...prev, ...newLeads]));

        setPagination(res.pagination || {}); // assuming API returns pagination
      }
    } catch (err) {
      console.error("Error loading leads:", err);
    } finally {
      setIsLoading(false);
      setIsApiPending(false);
    }
  };

  // Scroll listener
  useEffect(() => {
    const handleScroll = () => {
      if (!divRef.current) return;

      const { scrollTop, clientHeight, scrollHeight } = divRef.current;

      if (scrollTop + clientHeight >= scrollHeight - 180) {
        console.log("!isApiPendingRef.current", !isApiPendingRef.current);
        console.log(
          "pagination?.current_page < pagination?.last_page",
          pagination?.current_page < pagination?.last_page
        );
        if (
          !isApiPendingRef.current &&
          pagination?.current_page < pagination?.last_page
        ) {
          console.log("Second If");
          isApiPendingRef.current = true;
          setIsApiPending(true);
          setCardload(true);

          loadLeadsAndStages(pagination?.current_page + 1).finally(() => {
            isApiPendingRef.current = false;
            setIsApiPending(false);
            setCardload(false);
          });
        }
      }
    };

    const divElement = divRef.current;
    divElement?.addEventListener("scroll", handleScroll);

    return () => divElement?.removeEventListener("scroll", handleScroll);
  }, [pagination]);

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setIsLeadModalOpen(true);
  };

  const boxColor = {
    just_leads: { color: "#9D5FEF" },
    leads_with_deal: { color: "#5FE0EF" },
    lead_today_payment: { color: "#98ADFF" },
    contact_overdue_payment: { color: "#60E442" },
    not_contacted_2_days: { color: "#E9E03F" },
    untouched_leads: { color: "#335CFF" },
  };

  const filteredLeads = useMemo(() => leads, [leads]);
  const getLeadsByStage = (stageId) =>
    filteredLeads
      .filter((l) => l.current_stage === stageId)
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <>
      {isLoading ? (
        <InnerLoader />
      ) : (
        <div className="py-6 px-[15px] rounded-[10px]  border-[#e1E4Ea]  bg-[#fff]  h-full w-full overflow-hidden">
          <div
            ref={divRef}
            className="flex flex-col pb-2  overflow-auto "
            style={{ height: "calc(100%)" }}
          >
            <div className="flex-1">
              <div className="flex gap-6 h-full ">
                <AnimatePresence>
                  {stages.map((stage, index) => (
                    <motion.div
                      key={stage.id}
                      initial={{ opacity: 0, x: 50 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className="w-80 flex-shrink-0"
                    >
                      <AccountStageColumn
                        stage={stage}
                        counts={counts}
                        leads={getLeadsByStage(stage.id)}
                        onLeadClick={handleLeadClick}
                        isLoading={isLoading}
                        colors={boxColor[stage.id]}
                        isLast={stages?.length - 1 === index}
                        ProductList={ProductList}
                        DealStages={DealStages}
                        country={country}
                        cardLoad={cardLoad}
                        PackageList={PackageList}
                        UsersList={UserList}
                        SubProductList={SubProductList}
                        fetchSubProducts={fetchSubProducts}
                        fetchPackages={fetchPackages}
                        CustomerData={CustomerData}
                        BrandList={BrandList}
                        onSuccess={onSuccess}
                        onError={onError}
                      />
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>

              {isLeadModalOpen && (
                <AccountQuickView
                  onClose={() => {
                    setIsLeadModalOpen(false);
                    setSelectedLead({});
                  }}
                  data={{ ...selectedLead, userid: selectedLead?.id }}
                  ClientList={ClientList}
                  UserList={UserList}
                  onLoadMoreClients={onLoadMoreClients}
                  hasMoreClients={hasMoreClients}
                  isLoadingClients={isLoadingClients}
                  ProductList={ProductList}
                  PackageList={PackageList}
                  DealStages={DealStages}
                />
              )}
            </div>

            <Snackbar
              open={alertData.isShow}
              autoHideDuration={3000}
              onClose={handleCloseAlert}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            >
              <Alert
                onClose={handleCloseAlert}
                severity={alertData.type}
                sx={{ width: "100%" }}
              >
                {alertData.message}
              </Alert>
            </Snackbar>
          </div>
        </div>
      )}
    </>
  );
}
